import { apiFetch } from './apiClient';

/**
 * Shape returned by `GET /prompts`. Mirrors the server's SavedPromptDto exactly.
 *
 * Notable differences from the original handoff-doc draft:
 * - field is `icon: string` (not `iconKey: PromptIconKey`); the value is a
 *   sprite-symbol id like `"i-flag"` or `"i-pen"` matching `/public/icons.svg`.
 * - field is `sortOrder: number` (not `order`).
 * - `id` is a number, not a string.
 * - User-created prompts come back with `icon = "i-pen"` (DefaultPrompts.GenericIcon
 *   server-side). The icon is currently server-assigned only; if a future picker
 *   surfaces it'll need a server-side extension to accept the icon on POST/PUT.
 */
export interface SavedPrompt {
  id: number;
  text: string;
  icon: string;
  sortOrder: number;
}

export async function listPrompts(token: string): Promise<SavedPrompt[]> {
  const r = await apiFetch('/prompts', { token });
  if (!r.ok) throw new Error(`listPrompts: ${r.status}`);
  // Server already orders by sortOrder; a defensive re-sort guards against
  // a server change that didn't propagate to the doc.
  const rows: SavedPrompt[] = await r.json();
  return [...rows].sort((a, b) => a.sortOrder - b.sortOrder);
}

export async function addPrompt(token: string, text: string): Promise<SavedPrompt> {
  // Server assigns `icon = "i-pen"` and `sortOrder = max + 1`. No icon picker today.
  const r = await apiFetch('/prompts', {
    token,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!r.ok) throw new Error(`addPrompt: ${r.status}`);
  return r.json();
}

export async function updatePrompt(token: string, id: number, text: string): Promise<void> {
  // Server's UpdateSavedPromptRequest accepts text only — icon is not user-mutable today.
  const r = await apiFetch(`/prompts/${id}`, {
    token,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
  });
  if (!r.ok) throw new Error(`updatePrompt: ${r.status}`);
}

export async function deletePrompt(token: string, id: number): Promise<void> {
  const r = await apiFetch(`/prompts/${id}`, { token, method: 'DELETE' });
  if (!r.ok && r.status !== 404) throw new Error(`deletePrompt: ${r.status}`);
}

/**
 * Reorder by sending the full id sequence — server rewrites `sortOrder` to
 * match the array position. Body field is `orderedIds: number[]` (not the
 * `ids: string[]` the original draft assumed).
 */
export async function reorderPrompts(token: string, orderedIds: number[]): Promise<void> {
  const r = await apiFetch('/prompts/order', {
    token,
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ orderedIds }),
  });
  if (!r.ok) throw new Error(`reorderPrompts: ${r.status}`);
}
